import { useCallback, useEffect, useMemo, useState } from 'react';

import { useIsLoggedIn } from '@order/AuthMachine';
import type { ApplyRewardFailureCode } from '@order/graphql';
import { useCart, useChallengesTelemetry, useNewException } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';
import { getUrqlError } from '@order/utils';

import { useIsBagOpenFromReference } from '../../../components/Bag/hooks';
import {
  type RewardsQuery,
  useApplyRewardMutation,
  useRemoveRewardMutation,
  useRewardsQuery,
} from '../GraphQL/Rewards.generated';

export const useRewards = () => {
  const isLoggedIn = useIsLoggedIn();

  const isBagOpen = useIsBagOpenFromReference();

  const { cart } = useCart();
  const { id: orderId = '', ledger } = cart ?? {};
  const skipRewardsQuery = !isLoggedIn || !isBagOpen || !orderId;

  // ─── QUERY ───────────────────────────────────────────────────────

  const [rewardsResponse, fetchRewards] = useRewardsQuery({
    requestPolicy: 'network-only',
    pause: true,
  });

  const rewards = rewardsResponse.data?.rewards;
  const isFetchingRewards = rewardsResponse.fetching;
  const rewardsError = rewardsResponse.error;

  // ─── Mutations ───────────────────────────────────────────────────────

  const [applyRewardResponse, applyRewardMutation] = useApplyRewardMutation();
  const [removeRewardResponse, removeRewardMutation] =
    useRemoveRewardMutation();

  const isApplyingReward = applyRewardResponse.fetching;
  const applyRewardError = applyRewardResponse.error;
  const isRemovingReward = removeRewardResponse.fetching;

  // ─── State ───────────────────────────────────────────────────────────

  const applyRewardException = useApplyRewardException(applyRewardResponse);
  const cartReward = useCartReward(rewards);
  const [selectedRewardId, setSelectedRewardId] =
    useState<SelectedRewardId>(cartReward);

  const selectedRewardIndex = findSelectedRewardIndex(
    rewards,
    selectedRewardId,
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  const applyReward = useCallback(async () => {
    if (!selectedRewardId) {
      const cartHasAppliedReward = ledger?.discounts?.length;

      if (cartHasAppliedReward) {
        await removeRewardMutation({ input: { orderId } });
      }

      return;
    }

    return applyRewardMutation({
      input: { orderId, rewardId: selectedRewardId },
    });
  }, [
    applyRewardMutation,
    ledger?.discounts?.length,
    orderId,
    removeRewardMutation,
    selectedRewardId,
  ]);

  const fetchRewardsOnOrderIdChange = useCallback(() => {
    if (skipRewardsQuery) return;
    fetchRewards();
  }, [fetchRewards, skipRewardsQuery]);

  const clearSelectedReward = useCallback(() => {
    setSelectedRewardId('');
  }, []);

  // ─── Effects ─────────────────────────────────────────────────────────

  const {
    exceptionMessage: applyRewardExceptionMessage,
    hasNewException: applyRewardHasNewException,
  } = useNewException(applyRewardException);

  useEffect(() => {
    setSelectedRewardId((currentSelectedRewardId) => {
      const selectedRewardIsStillAvailable = rewards?.some(
        (reward) => reward.id === currentSelectedRewardId,
      );

      return selectedRewardIsStillAvailable ? currentSelectedRewardId : '';
    });
  }, [rewards, selectedRewardId]);

  useEffect(() => {
    setSelectedRewardId(cartReward);
  }, [cartReward]);

  useEffect(() => {
    if (!isBagOpen) return;
    setSelectedRewardId(cartReward);
  }, [cartReward, isBagOpen]);

  useEffect(() => {
    if (!applyRewardException) return;

    clearSelectedReward();
    fetchRewards();
  }, [applyRewardException, fetchRewards, clearSelectedReward]);

  useEffect(fetchRewardsOnOrderIdChange, [fetchRewardsOnOrderIdChange]);

  const { handleRewardErrorTelemetry } = useChallengesTelemetry();

  useEffect(() => {
    if (!applyRewardException?.message) return;
    handleRewardErrorTelemetry(
      applyRewardException.message,
      getUrqlError(applyRewardError),
    );
  }, [applyRewardException, applyRewardError, handleRewardErrorTelemetry]);

  // ─────────────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      applyReward,
      applyRewardError,
      applyRewardException,
      applyRewardExceptionMessage,
      applyRewardHasNewException,
      clearSelectedReward,
      fetchRewards,
      isApplyingReward,
      isFetchingRewards,
      isRemovingReward,
      rewards,
      rewardsError,
      selectedRewardId,
      selectedRewardIndex,
      setSelectedRewardId,
    }),
    [
      applyReward,
      applyRewardError,
      applyRewardException,
      applyRewardExceptionMessage,
      applyRewardHasNewException,
      clearSelectedReward,
      fetchRewards,
      isApplyingReward,
      isFetchingRewards,
      isRemovingReward,
      rewards,
      rewardsError,
      selectedRewardId,
      selectedRewardIndex,
    ],
  );
};

// ─── HOOKS ──────────────────────────────────────────────────────────────────────

const useCartReward = (rewards: Rewards) => {
  const { cart } = useCart();
  const cartDiscounts = cart?.ledger?.discounts ?? [];
  const cartRewardId = rewards?.find((reward) => {
    return cartDiscounts.some((discount) => discount.name === reward.name);
  })?.id;

  return cartRewardId ?? '';
};

const useApplyRewardException = (
  response: ReturnType<typeof useApplyRewardMutation>[0],
) => {
  const { t } = useLocalizationContext();

  const responseData = response.data?.applyReward;
  const responseError = response.error;
  const isRewardNotApplied = responseData?.__typename === 'RewardNotApplied';

  const knownExceptions: Record<ApplyRewardFailureCode, string> = useMemo(
    () => ({
      REWARD_NOT_APPLICABLE: t('challenges.rewards.error.not-applicable'),
      LOYALTY_PROGRAM_UNAVAILABLE: t(
        'challenges.rewards.error.loyalty-program-unavailable',
      ),
      LOYALTY_NOT_PROCESSED: t(
        'challenges.rewards.error.loyalty-not-processed',
      ),
      REWARD_EXPIRED: t('challenges.rewards.error.expired'),
      REWARD_NOT_FOUND: t('challenges.rewards.error.not-found'),
      RESTAURANT_NOT_ACCEPTING_ORDERS: t(
        'challenges.rewards.error.restaurant-not-accepting-orders',
      ),
    }),
    [t],
  );

  return useMemo(() => {
    if (responseError) {
      return {
        message: t('challenges.rewards.error.server-error'),
      };
    }

    if (!isRewardNotApplied) return undefined;

    const code = responseData?.failureCode;
    const message = knownExceptions?.[code];

    return { message };
  }, [isRewardNotApplied, knownExceptions, responseData, responseError, t]);
};

// ─── UTILS ──────────────────────────────────────────────────────────────────────

const findSelectedRewardIndex = (
  rewards: Rewards,
  selectedRewardId: string,
) => {
  const selectedRewardIndex =
    rewards?.findIndex((reward) => reward.id === selectedRewardId) ?? -1;

  return selectedRewardIndex >= 0 ? selectedRewardIndex : undefined;
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type Rewards = RewardsQuery['rewards'] | undefined;

type SelectedRewardId = string;
